// Lightweight event CRUD routes use Lambda under /api/*.
// Gallery read URLs and photo request/share CRUD use Lambda.
// Face, search, uploads, and grouping-worker routes stay on the EC2/Fargate
// core backend under /api/core/*.
import { apiClient as lambdaApiClient, coreApiClient, BACKEND_URL as LAMBDA_BACKEND_URL } from '../../services/api';
import { compressEventImage, compressEventImagesInChunks, compressedImageName, uniqueCompressedImageName, computeUcMd5Hash, extractCaptureMetadata } from '../../utils/compressEventImage';

// Alias so the rest of the file is unchanged.
const apiClient = coreApiClient;

function _activeToken() {
  return localStorage.getItem('org_auth_token') || localStorage.getItem('auth_token') || '';
}

function _token() {
  return encodeURIComponent(_activeToken());
}

function _authHeaders(json = false) {
  const token = _activeToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

function _formValue(formData, key) {
  return formData.get(key) || '';
}

// Wraps the upload/grouping-lifecycle API calls (used by both EventPage.js's
// organiser flow and UploadLinkPage.js's guest flow) with request/response
// logging - mirrors CmApiClient.kt's `-> METHOD path` / `<- METHOD path OK
// (Nms)` pattern on the native side, so a device's browser/webview console
// shows the same shape of trace as `adb logcat -s CmApiClient:*` does for the
// native background-upload pipeline. Deliberately NOT applied to every
// eventApi call (gallery reads, face-naming, etc.) - just the upload/grouping
// surface this task is about.
function _apiLog(label, promise) {
  const startedAt = Date.now();
  console.log(`[EventAPI] -> ${label}`);
  return promise.then((res) => {
    console.log(`[EventAPI] <- ${label} OK (${Date.now() - startedAt}ms)`);
    return res;
  }).catch((err) => {
    console.error(`[EventAPI] <- ${label} FAILED (${Date.now() - startedAt}ms):`, err?.message || err);
    throw err;
  });
}

async function _uploadPhotoRequestReference(eventId, file, { inviteCode = '', requestId = '' } = {}) {
  if (!file) return null;
  const compressed = await compressEventImage(file);
  const prepareRes = await fetch(`${LAMBDA_BACKEND_URL}/api/events/${eventId}/photo-requests/upload-url`, {
    method: 'POST',
    headers: _authHeaders(true),
    body: JSON.stringify({
      invite_code: inviteCode,
      request_id: requestId,
      file: {
        name: compressedImageName(file.name),
        size: compressed.size,
        content_type: compressed.type || 'image/jpeg',
      },
    }),
  });
  const prepared = await prepareRes.json();
  if (!prepareRes.ok) throw new Error(prepared.error || 'Failed to prepare reference photo upload');
  const uploadRes = await fetch(prepared.upload.url, {
    method: 'PUT',
    headers: { 'Content-Type': prepared.upload.content_type || 'image/jpeg' },
    body: compressed,
  });
  if (!uploadRes.ok) throw new Error('Failed to upload reference photo');
  return prepared;
}

// Attaches one secondary reference photo to an existing photo request or
// reference set, mirroring _uploadPhotoRequestReference's two-phase S3
// upload (prepare presigned URL -> PUT -> finalize) but for the
// MAX_SECONDARY_REFERENCE_PHOTOS-capped extra-photos endpoints instead of
// the single required primary. `kind` picks the URL family; `ownerId` is the
// request_id or set_id the photo attaches to.
async function _uploadSecondaryReferencePhoto(eventId, kind, ownerId, file, { inviteCode = '', isPrimary = false } = {}) {
  if (!file) return null;
  const compressed = await compressEventImage(file);
  const base = kind === 'set'
    ? `${LAMBDA_BACKEND_URL}/api/events/${eventId}/reference-sets/${encodeURIComponent(ownerId)}`
    : `${LAMBDA_BACKEND_URL}/api/events/${eventId}/photo-requests/${encodeURIComponent(ownerId)}`;
  const uploadPathSegment = kind === 'set' ? 'photos' : 'secondary-photos';
  const prepareRes = await fetch(`${base}/${uploadPathSegment}/upload-url`, {
    method: 'POST',
    headers: _authHeaders(true),
    body: JSON.stringify({
      invite_code: inviteCode,
      file: {
        name: compressedImageName(file.name),
        size: compressed.size,
        content_type: compressed.type || 'image/jpeg',
      },
    }),
  });
  const prepared = await prepareRes.json();
  if (!prepareRes.ok) throw new Error(prepared.error || 'Failed to prepare reference photo upload');
  const uploadRes = await fetch(prepared.upload.url, {
    method: 'PUT',
    headers: { 'Content-Type': prepared.upload.content_type || 'image/jpeg' },
    body: compressed,
  });
  if (!uploadRes.ok) throw new Error('Failed to upload reference photo');
  const finalizeRes = await fetch(`${base}/${uploadPathSegment}`, {
    method: 'POST',
    headers: _authHeaders(true),
    body: JSON.stringify({
      invite_code: inviteCode,
      tmp_key: prepared.upload.key,
      photo_key: prepared.photo_key,
      ...(kind === 'set' ? { is_primary: isPrimary } : {}),
    }),
  });
  const finalized = await finalizeRes.json();
  if (!finalizeRes.ok) throw new Error(finalized.error || 'Failed to attach reference photo');
  return finalized;
}

function _photoRequestPayload(formData, extra = {}) {
  return {
    invite_code: _formValue(formData, 'invite_code'),
    name: _formValue(formData, 'name'),
    email_address: _formValue(formData, 'email_address'),
    phone_number: _formValue(formData, 'phone_number'),
    whatsapp_number: _formValue(formData, 'whatsapp_number'),
    country_code: _formValue(formData, 'country_code'),
    whatsapp_country_code: _formValue(formData, 'whatsapp_country_code'),
    notes: _formValue(formData, 'notes'),
    consent_reference_face_match: _formValue(formData, 'consent_reference_face_match'),
    policy_version: _formValue(formData, 'policy_version'),
    on_behalf_of_minor: _formValue(formData, 'on_behalf_of_minor'),
    on_behalf_of_relationship: _formValue(formData, 'on_behalf_of_relationship'),
    subject_name: _formValue(formData, 'subject_name'),
    ...extra,
  };
}

const eventApi = {
  // ── Event CRUD ───────────────────────────────────────────────────────────────
  createEvent: (data) => lambdaApiClient.post('/events', data).then(r => r.data),
  listMyEvents: () => lambdaApiClient.get('/my-events').then(r => r.data),
  getEvent: (eventId) => lambdaApiClient.get(`/events/${eventId}`).then(r => r.data),
  deleteEvent: (eventId) => apiClient.delete(`/events/${eventId}`).then(r => r.data),
  updateEventDetails: (eventId, data) => apiClient.put(`/events/${eventId}/details`, data).then(r => r.data),
  updateAutoPopulateSetting: (eventId, enabled, { consent_reference_face_match } = {}) =>
    lambdaApiClient.patch(`/events/${eventId}/auto-populate-setting`, { enabled, consent_reference_face_match }).then(r => r.data),
  updateAutoShareNewSubmissions: (eventId, enabled) =>
    lambdaApiClient.patch(`/events/${eventId}/auto-share-new-submissions`, { enabled }).then(r => r.data),
  joinEvent: (invite_code) => lambdaApiClient.post('/events/join', { invite_code }).then(r => r.data),

  // ── Grouping ─────────────────────────────────────────────────────────────────

  // Lambda-based endpoints (proxy to Fargate or EC2 depending on GROUPING_BACKEND config).
  // initiateGrouping: credit hold only — does NOT start the container.
  // Container start is triggered by preWarmGroupingServer (2 batches before the end, or
  // immediately for uploads ≤ 100 images).
  initiateGrouping: (eventId, mode = 'initial') =>
    _apiLog(`initiateGrouping event=${eventId} mode=${mode}`,
      lambdaApiClient.post(`/events/${eventId}/initiate-grouping`, { mode }).then(r => r.data)),
  // preWarmGroupingServer starts the container/instance ~2 batches before the upload ends.
  preWarmGroupingServer: (eventId, mode = 'initial') =>
    _apiLog(`preWarmGroupingServer event=${eventId} mode=${mode}`,
      lambdaApiClient.post(`/events/${eventId}/grouping-pre-warm`, { mode }).then(r => r.data)),
  // groupingKickoff replaces startServerGroupingFromS3 - called after S3 upload completes.
  groupingKickoff: (eventId, payload) =>
    _apiLog(`groupingKickoff event=${eventId} mode=${payload?.mode}`,
      lambdaApiClient.post(`/events/${eventId}/grouping-kickoff`, {
        ...payload,
        consent_event_face_grouping: true,
      }).then(r => r.data)),
  // addPhotosKickoff replaces startAddPhotosFromS3 - called after add-photos S3 upload.
  addPhotosKickoff: (eventId, payload) =>
    _apiLog(`addPhotosKickoff event=${eventId}`,
      lambdaApiClient.post(`/events/${eventId}/add-photos-kickoff`, {
        ...payload,
        consent_event_face_grouping: true,
      }).then(r => r.data)),
  groupingUploadProgress: (eventId, payload) =>
    _apiLog(`groupingUploadProgress event=${eventId} uploaded=${payload?.uploaded_count}/${payload?.total_files}`,
      lambdaApiClient.post(`/events/${eventId}/grouping-upload-progress`, {
        ...payload,
        consent_event_face_grouping: true,
      }).then(r => r.data)),
  groupingUploadComplete: (eventId, payload) =>
    _apiLog(`groupingUploadComplete event=${eventId} mode=${payload?.mode} uploaded=${payload?.uploaded_count}`,
      lambdaApiClient.post(`/events/${eventId}/grouping-upload-complete`, {
        ...payload,
        consent_event_face_grouping: true,
      }).then(r => r.data)),

  // Server-side Google Drive import (see drive_import_service.py) - replaces
  // the old client-side "download from Drive, compress, PUT to S3" flow.
  // `mode` is 'group_now' (feeds the same grouping-upload-progress/complete
  // bookkeeping runOrganiserUploadBatches uses) or 'upload_only' (lands in
  // the photo-submissions pool, grouped later via ingestPhotoSubmissions).
  scheduleDriveImport: (eventId, payload) =>
    _apiLog(`scheduleDriveImport event=${eventId} mode=${payload?.mode} files=${payload?.drive_files?.length}`,
      apiClient.post(`/events/${eventId}/drive-import`, payload).then(r => r.data)),
  getDriveImportStatus: (eventId, jobId) =>
    apiClient.get(`/events/${eventId}/drive-import/${encodeURIComponent(jobId)}/status`).then(r => r.data),
  cancelDriveImport: (eventId, jobId) =>
    apiClient.post(`/events/${eventId}/drive-import/${encodeURIComponent(jobId)}/cancel`).then(r => r.data),

  getOriginalUploadUrls: (eventId, payload) =>
    _apiLog(`getOriginalUploadUrls event=${eventId} files=${payload?.files?.length}`,
      lambdaApiClient.post(`/events/${eventId}/original-upload-urls`, payload).then(r => r.data)),
  rebuildSearchIndex: (eventId) =>
    apiClient.post(`/events/${eventId}/rebuild-search-index`).then(r => r.data),
  invalidateSearchCache: (eventId) =>
    apiClient.post(`/events/${eventId}/invalidate-search-cache`).then(r => r.data),
  // Status and cancel now go through Lambda (which proxies to Fargate or EC2).
  getGroupingStatus: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/grouping-status`).then(r => r.data),
  cancelGrouping: (eventId) =>
    _apiLog(`cancelGrouping event=${eventId}`,
      lambdaApiClient.post(`/events/${eventId}/cancel-grouping`).then(r => r.data)),

  // ── Event gallery ─────────────────────────────────────────────────────────────
  getGalleryMetadata: (eventId, opts = {}) =>
    lambdaApiClient.get(`/events/${eventId}/gallery-metadata`, {
      params: opts.includeUnprocessed ? { include_unprocessed: 1 } : undefined,
    }).then(r => r.data),
  getGroupNames: (eventId) =>
    apiClient.get(`/events/${eventId}/group-names`).then(r => r.data),
  saveGroupNames: (eventId, names) =>
    apiClient.post(`/events/${eventId}/group-names`, names).then(r => r.data),
  getGroupAssignments: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/group-assignments`).then(r => r.data),
  saveGroupAssignment: (eventId, body) =>
    apiClient.post(`/events/${eventId}/group-assignments`, body).then(r => r.data),
  updatePhotoMetadata: (eventId, photoId, payload) =>
    apiClient.put(`/events/${eventId}/photos/${encodeURIComponent(photoId)}/metadata`, payload).then(r => r.data),
  deletePhotos: (eventId, photoIds) =>
    apiClient.delete(`/events/${eventId}/photos`, { data: { photo_ids: photoIds } }).then(r => r.data),
  searchEventPhotos: (eventId, q, searchMode) =>
    apiClient.get('/moments/photos', { params: { event_id: eventId, q, search_mode: searchMode } }).then(r => r.data),

  // ── Photo requests ────────────────────────────────────────────────────────────
  listPhotoRequests: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/photo-requests`).then(r => r.data),
  getPhotoRequest: (eventId, requestId) =>
    lambdaApiClient.get(`/events/${eventId}/photo-requests/${requestId}`).then(r => r.data),
  deletePhotoRequest: (eventId, requestId) =>
    lambdaApiClient.delete(`/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}`).then(r => r.data),
  saveSelections: (eventId, requestId, payload) =>
    lambdaApiClient.post(`/events/${eventId}/photo-requests/${requestId}/selections`, payload).then(r => r.data),
  autoPopulate: (eventId, requestId) =>
    apiClient.post(`/events/${eventId}/photo-requests/${requestId}/auto-populate`).then(r => r.data),
  updateMyRequestFields: (eventId, requestId, payload) =>
    lambdaApiClient.put(`/events/${eventId}/my-requests/${encodeURIComponent(requestId)}`, payload).then(r => r.data),
  // Organiser-only: replace a request's primary reference selfie (uploads to
  // S3 via the same two-phase flow as a fresh submission, then finalizes
  // against this existing request_id instead of creating a new one).
  updateRequestReferencePhoto: async (eventId, requestId, file) => {
    const prepared = await _uploadPhotoRequestReference(eventId, file, { requestId });
    return lambdaApiClient.put(`/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}/reference-photo`, {
      reference_photo_key: prepared.upload.key,
    }).then(r => r.data);
  },

  // Secondary (non-primary) reference photos on a photo request - up to
  // MAX_SECONDARY_REFERENCE_PHOTOS, used alongside the primary selfie by
  // Smart Auto-Match. `inviteCode` lets a guest attach these right at
  // submission time, before they have an account; omit it for an
  // authenticated owner/organiser adding one later.
  listRequestSecondaryPhotos: (eventId, requestId) =>
    lambdaApiClient.get(`/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}/secondary-photos`).then(r => r.data),
  addRequestSecondaryPhoto: (eventId, requestId, file, { inviteCode = '' } = {}) =>
    _uploadSecondaryReferencePhoto(eventId, 'request', requestId, file, { inviteCode }),
  deleteRequestSecondaryPhoto: (eventId, requestId, photoKey) =>
    lambdaApiClient.delete(`/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}/secondary-photos/${encodeURIComponent(photoKey)}`).then(r => r.data),
  updateRequestMatchAllPhotos: (eventId, requestId, enabled) =>
    lambdaApiClient.patch(`/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}/match-all-photos`, { enabled }).then(r => r.data),
  updateRequestAutoAddNewSubmissions: (eventId, requestId, enabled) =>
    lambdaApiClient.patch(`/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}/auto-add-new-submissions`, { enabled }).then(r => r.data),

  // Organiser-managed, event-owned reference sets (e.g. "Bride & Groom") that
  // Smart Auto-Match folds into every request's match while enabled - see
  // services/auto_populate_service.py. mode is 'faces' or 'all_photos'.
  listReferenceSets: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/reference-sets`).then(r => r.data),
  createReferenceSet: (eventId, label, mode = 'faces') =>
    lambdaApiClient.post(`/events/${eventId}/reference-sets`, { label, mode }).then(r => r.data),
  updateReferenceSet: (eventId, setId, fields) =>
    lambdaApiClient.patch(`/events/${eventId}/reference-sets/${encodeURIComponent(setId)}`, fields).then(r => r.data),
  deleteReferenceSet: (eventId, setId) =>
    lambdaApiClient.delete(`/events/${eventId}/reference-sets/${encodeURIComponent(setId)}`).then(r => r.data),
  addReferenceSetPhoto: (eventId, setId, file, { isPrimary = false } = {}) =>
    _uploadSecondaryReferencePhoto(eventId, 'set', setId, file, { isPrimary }),
  deleteReferenceSetPhoto: (eventId, setId, photoKey) =>
    lambdaApiClient.delete(`/events/${eventId}/reference-sets/${encodeURIComponent(setId)}/photos/${encodeURIComponent(photoKey)}`).then(r => r.data),

  // Do-not-share deletion requests
  listDeletionRequests: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/deletion-requests`).then(r => r.data),
  getDeletionRequest: (eventId, requestId) =>
    lambdaApiClient.get(`/events/${eventId}/deletion-requests/${encodeURIComponent(requestId)}`).then(r => r.data),
  saveDeletionRequestSelections: (eventId, requestId, payload) =>
    lambdaApiClient.post(`/events/${eventId}/deletion-requests/${encodeURIComponent(requestId)}/selections`, payload).then(r => r.data),
  autoPopulateDeletionRequest: (eventId, requestId) =>
    apiClient.post(`/events/${eventId}/deletion-requests/${encodeURIComponent(requestId)}/auto-populate`).then(r => r.data),

  // ── URL helpers (direct backend, no apiClient - used in <img src="..."> ) ─────
  // Token is appended as ?token= so the server can authenticate requests from <img> tags.
  listPhotoCircles: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/photo-circles`).then(r => r.data),
  getPhotoCircle: (eventId, circleId) =>
    lambdaApiClient.get(`/events/${eventId}/photo-circles/${encodeURIComponent(circleId)}`).then(r => r.data),
  saveCircleSelections: (eventId, circleId, payload) =>
    lambdaApiClient.post(`/events/${eventId}/photo-circles/${encodeURIComponent(circleId)}/selections`, payload).then(r => r.data),

  thumbUrl: (eventId, photoId) =>
    `${LAMBDA_BACKEND_URL}/api/events/${eventId}/photo-thumbnail?photo_id=${encodeURIComponent(photoId)}&token=${_token()}`,
  previewUrl: (eventId, photoId) =>
    `${LAMBDA_BACKEND_URL}/api/events/${eventId}/photo-preview?photo_id=${encodeURIComponent(photoId)}&token=${_token()}`,
  originalUrl: (eventId, photoId) =>
    `${LAMBDA_BACKEND_URL}/api/events/${eventId}/photo-original?photo_id=${encodeURIComponent(photoId)}&token=${_token()}`,
  // "Uncompressed original" - resolves uc_org_url server-side and hands back
  // a presigned S3 URL for it (valid even before a Drive-backed original has
  // finished relaying into the cache - 404s until it lands, 200 once cached;
  // see backend/routes/uc_originals.py). Served by the EC2/core backend (not
  // Lambda) so it can submit the relay job and respond immediately rather
  // than blocking on a Drive transfer that can take minutes. Callers poll
  // the returned URL directly against S3 - see utils/ucOriginalDownload.
  requestUcOriginalDownload: (eventId, photoId) =>
    apiClient.get(`/events/${eventId}/photo-uc-original`, { params: { photo_id: photoId } }).then(r => r.data),
  setDriveOriginalRefs: (eventId, refs) =>
    lambdaApiClient.post(`/events/${eventId}/drive-original-refs`, { refs }).then(r => r.data),
  // Original (pre-compression) filename + MD5 hash + capture date/EXIF per
  // photo - see compressEventImage.js's computeUcMd5Hash/extractCaptureMetadata
  // and cm_backend_utils/uc_file_metadata.py. `uploadLinkToken`, if given, lets
  // an anonymous guest submission record this the same way an organiser upload
  // does (see _resolve_submission_access server-side) - harmless to omit for an
  // organiser/attendee call, which authenticates via JWT instead. `isSubmission`
  // marks this batch as having come through the photo-submissions flow (guest
  // upload-link or in-app submission, as opposed to the organiser's own direct
  // grouping-upload) - only those batches get submitted_by_* stamped, resolved
  // server-side (never trusted from the client); `uploaderName` is only read
  // when `isSubmission` and the caller is an anonymous (not logged-in) guest.
  setUcFileMetadata: (eventId, entries, { uploadLinkToken = '', isSubmission = false, uploaderName = '' } = {}) =>
    lambdaApiClient.post(`/events/${eventId}/uc-file-metadata`, {
      entries,
      ...(uploadLinkToken ? { upload_link_token: uploadLinkToken } : {}),
      ...(isSubmission ? { is_submission: true } : {}),
      ...(uploaderName ? { uploader_name: uploaderName } : {}),
    }).then(r => r.data),
  // "Match Originals from Drive" - see EventPage.js's resolveDriveChecksums.
  // `entries` is [{drive_file_id, name, md5_checksum}], metadata-only (no
  // file content was downloaded to build it). Organiser-only.
  matchDriveOriginals: (eventId, entries) =>
    lambdaApiClient.post(`/events/${eventId}/match-drive-originals`, { entries }).then(r => r.data),
  groupThumbUrl: (eventId, groupId) =>
    `${LAMBDA_BACKEND_URL}/api/events/${eventId}/group-thumbnail/${encodeURIComponent(groupId)}?token=${_token()}`,
  requestPhotoUrl: (eventId, requestId) =>
    `${LAMBDA_BACKEND_URL}/api/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}/reference-photo`,
  getRequestPhotoBlobUrl: async (eventId, requestId, cacheBust) => {
    const res = await lambdaApiClient.get(
      `/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}/reference-photo`,
      {
        responseType: 'blob',
        params: cacheBust ? { t: cacheBust } : undefined,
      }
    );
    return URL.createObjectURL(res.data);
  },

  // ── Photo/deletion request reference photos upload directly to S3 first ─────
  submitPhotoRequest: (eventId, formData) => {
    const referencePhoto = formData.get('reference_photo');
    // If no reference photo, submit the request without selfie (optional selfie flow)
    if (!referencePhoto) {
      return fetch(`${LAMBDA_BACKEND_URL}/api/events/${eventId}/photo-requests`, {
        method: 'POST',
        headers: _authHeaders(true),
        body: JSON.stringify({
          ..._photoRequestPayload(formData),
          skip_contact_required: Boolean(formData.get('skip_contact_required')),
          invitation_id: _formValue(formData, 'invitation_id') || undefined,
        }),
      }).then(r => r.json());
    }
    return _uploadPhotoRequestReference(eventId, referencePhoto, {
      inviteCode: _formValue(formData, 'invite_code'),
    }).then(prepared => fetch(`${LAMBDA_BACKEND_URL}/api/events/${eventId}/photo-requests`, {
      method: 'POST',
      headers: _authHeaders(true),
      body: JSON.stringify(_photoRequestPayload(formData, {
        request_id: prepared.request_id,
        reference_photo_key: prepared.upload.key,
      })),
    }).then(r => r.json()));
  },
  submitDeletionRequest: (eventId, formData) => {
    const token = _activeToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const referencePhoto = formData.get('reference_photo');
    if (!referencePhoto) return Promise.resolve({ error: 'reference_photo is required' });
    return compressEventImage(referencePhoto)
      .then(file => fetch(`${LAMBDA_BACKEND_URL}/api/events/${eventId}/deletion-requests/upload-url`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invite_code: formData.get('invite_code') || '',
          file: {
            name: compressedImageName(referencePhoto.name),
            size: file.size,
            content_type: file.type || 'image/jpeg',
          },
        }),
      })
        .then(r => r.json().then(data => {
          if (!r.ok) throw new Error(data.error || 'Failed to prepare upload');
          return { file, prepared: data };
        })))
      .then(({ file, prepared }) => fetch(prepared.upload.url, {
        method: 'PUT',
        headers: { 'Content-Type': prepared.upload.content_type || 'image/jpeg' },
        body: file,
      }).then(r => {
        if (!r.ok) throw new Error('Failed to upload reference photo');
        return prepared;
      }))
      .then(prepared => fetch(`${LAMBDA_BACKEND_URL}/api/events/${eventId}/deletion-requests`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invite_code: formData.get('invite_code') || '',
          name: formData.get('name') || '',
          email_address: formData.get('email_address') || '',
          notes: formData.get('notes') || '',
          consent_deletion_face_match: formData.get('consent_deletion_face_match') || '',
          policy_version: formData.get('policy_version') || '',
          on_behalf_of_minor: formData.get('on_behalf_of_minor') || '',
          on_behalf_of_relationship: formData.get('on_behalf_of_relationship') || '',
          subject_name: formData.get('subject_name') || '',
          request_id: prepared.request_id,
          reference_photo_key: prepared.upload.key,
        }),
      }).then(r => r.json()));
  },

  // ── Public lookup by invite code (no auth) ────────────────────────────────
  lookupEvent: (invite_code) =>
    lambdaApiClient.get(`/events/lookup?invite_code=${encodeURIComponent(invite_code)}`)
      .then(r => r.data),
  // ── Public QR code -> invite code lookup (no auth) ────────────────────────
  lookupPublicQr: (qr) =>
    lambdaApiClient.get(`/public-qr/lookup?qr=${encodeURIComponent(qr)}`)
      .then(r => r.data),

  // ── Photo sharing ─────────────────────────────────────────────────────────
  generateShareLink: (eventId, requestId) =>
    lambdaApiClient.post(`/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}/share-link`)
      .then(r => r.data),
  sendPhotosEmail: (eventId, requestId) =>
    lambdaApiClient.post(`/events/${eventId}/photo-requests/${encodeURIComponent(requestId)}/send-photos-email`)
      .then(r => r.data),
  getShareData: (token) =>
    lambdaApiClient.get(`/share/${encodeURIComponent(token)}`).then(r => r.data),
  getShareOriginals: (token) =>
    lambdaApiClient.get(`/share/${encodeURIComponent(token)}/originals`).then(r => r.data),
  // Only ever includes photo IDs that have a genuinely distinct uncompressed
  // original (see face_group/main.py / event_photo_uc_original) - the visitor
  // can only "download all uncompressed" the photos that actually have one.
  // Served by EC2 (not Lambda), alongside the relay routes themselves, so
  // Drive-backed URLs in the response can point back at this same service.
  getShareUcOriginals: (token) =>
    apiClient.get(`/share/${encodeURIComponent(token)}/uc-originals`).then(r => r.data),

  // ── Batch presigned URLs for gallery media ────────────────────────────────
  getPresignedUrls: (eventId, photoIds, includeGroups, groupIds) => {
    if (Array.isArray(photoIds)) {
      return lambdaApiClient.post(`/events/${eventId}/gallery-presigned-urls`, {
        photo_ids: photoIds,
        include_groups: Boolean(includeGroups),
        group_ids: Array.isArray(groupIds) ? groupIds : [],
      }).then(r => r.data);
    }
    return lambdaApiClient.get(`/events/${eventId}/gallery-presigned-urls`).then(r => r.data);
  },

  // ── Add photos (incremental regroup) ─────────────────────────────────────
  startAddPhotosFromS3: (eventId, payload) =>
    apiClient.post(`/events/${eventId}/add-photos-start`, {
      ...payload,
      consent_event_face_grouping: true,
    }).then(r => r.data),

  // ── Face blur ─────────────────────────────────────────────────────────────
  blurFace: (eventId, body) =>
    apiClient.post(`/events/${eventId}/blur-face`, body).then(r => r.data),
  acceptBlur: (eventId, body) =>
    apiClient.post(`/events/${eventId}/accept-blur`, body).then(r => r.data),
  revertBlur: (eventId, photoId) =>
    lambdaApiClient.post(`/events/${eventId}/revert-blur`, { photo_id: photoId }).then(r => r.data),
  blurFaceGroup: (eventId, faceGroupId) =>
    apiClient.post(`/events/${eventId}/blur-face-group`, { face_group_id: faceGroupId }).then(r => r.data),
  blurFaceGroupStatus: (eventId, jobId) =>
    apiClient.get(`/events/${eventId}/blur-face-group/status/${jobId}`).then(r => r.data),
  revertBlurGroup: (eventId, faceGroupId) =>
    apiClient.post(`/events/${eventId}/revert-blur-group`, { face_group_id: faceGroupId }).then(r => r.data),
  revertBlurGroupStatus: (eventId, jobId) =>
    apiClient.get(`/events/${eventId}/revert-blur-group/status/${jobId}`).then(r => r.data),

  // ── Face re-grouping ──────────────────────────────────────────────────────
  regroupFace: (eventId, body) =>
    apiClient.post(`/events/${eventId}/regroup-face`, body).then(r => r.data),
  reclusterGroup: (eventId, group) =>
    apiClient.post(`/events/${eventId}/recluster-group`, { group }).then(r => r.data),
  reclusterGroupStatus: (eventId, jobId) =>
    apiClient.get(`/events/${eventId}/recluster-group/status/${jobId}`).then(r => r.data),
  getNamedFaces: () =>
    lambdaApiClient.get('/memory/named-faces').then(r => r.data),
  getMemoryFaceThumbnailUrls: (keys) =>
    lambdaApiClient.post('/memory/face-thumbnail-urls', { keys }).then(r => r.data),

  // ── Current user's request for an event ──────────────────────────────────
  // ── Attendee face naming (saves to attendee's own account memory) ────────
  getMyGroupNames: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/my-group-names`).then(r => {
      const d = r.data;
      // Support both old flat format and new {group_names, embedding_model, has_embeddings} format
      if (d && typeof d.group_names === 'object') return d;
      return { group_names: d || {}, embedding_model: null, has_embeddings: false };
    }),
  saveMyGroupNames: (eventId, names) =>
    apiClient.post(`/events/${eventId}/my-group-names`, names).then(r => r.data),
  autoPopulateMyGroupNames: (eventId) =>
    apiClient.post(`/events/${eventId}/attendee-auto-populate-group-names`).then(r => r.data),
  learnFaceGroup: (eventId, body) =>
    apiClient.post(`/events/${eventId}/learn-face-group`, body).then(r => r.data),
  getLearnFaceGroupStatus: (eventId, jobId) =>
    apiClient.get(`/events/${eventId}/learn-face-group/status/${jobId}`).then(r => r.data),
  autoPopulateGroupNames: (eventId) =>
    apiClient.post(`/events/${eventId}/auto-populate-group-names`).then(r => r.data),
  getAutoPopulateGroupNamesStatus: (eventId, jobId) =>
    apiClient.get(`/events/${eventId}/auto-populate-group-names/status/${jobId}`).then(r => r.data),
  rebuildGroupEmbeddings: (eventId) =>
    apiClient.post(`/events/${eventId}/rebuild-group-embeddings`).then(r => r.data),
  getRebuildGroupEmbeddingsStatus: (eventId, jobId) =>
    apiClient.get(`/events/${eventId}/rebuild-group-embeddings/status/${jobId}`).then(r => r.data),

  getMyRequest: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/my-request`).then(r => r.data),
  getMyRequests: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/my-requests`).then(r => r.data),
  getMyPhotos: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/my-photos`).then(r => r.data),
  deleteMyRequest: (eventId, requestId) =>
    lambdaApiClient.delete(`/events/${eventId}/my-requests/${encodeURIComponent(requestId)}`).then(r => r.data),
  updateMySpecificRequest: async (eventId, requestId, formData) => {
    const referencePhoto = formData.get('reference_photo');
    let upload = null;
    if (referencePhoto) {
      upload = await _uploadPhotoRequestReference(eventId, referencePhoto, { requestId });
    }
    const res = await fetch(`${LAMBDA_BACKEND_URL}/api/events/${eventId}/my-requests/${encodeURIComponent(requestId)}`, {
      method: 'PUT',
      headers: _authHeaders(true),
      body: JSON.stringify(_photoRequestPayload(formData, upload ? {
        reference_photo_key: upload.upload.key,
      } : {})),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.message || 'Failed to update submission');
    return data;
  },
  // ── Photo submissions (attendee-uploaded photos) ──────────────────────────
  // Every submission path (this one, and uploadPhotoSubmissionsViaLink below)
  // compresses each file client-side via compressEventImage - same 2048px/
  // quality-0.92 preset used elsewhere in the app (grouping uploads, reference
  // selfies) - before requesting a presigned URL, so submissions never land
  // in S3 as raw, full-resolution camera files. If compression itself fails
  // for a given file (corrupt image, unsupported format), that file falls
  // back to its original bytes rather than blocking the whole batch.
  uploadPhotoSubmissions: (eventId, formData, signal, hooks = {}) =>
    {
      const onFileCompressed = hooks.onFileCompressed || (() => {});
      const onFileUploaded = hooks.onFileUploaded || (() => {});
      const rawFiles = formData.getAll('files');
      const uploadId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      // Hash + read capture date/EXIF from each file's RAW bytes before
      // compressEventImagesInChunks touches them - compression is lossy/
      // irreversible, so this is the last point the original's true identity
      // (and its EXIF block) is available. See computeUcMd5Hash's/
      // extractCaptureMetadata's own doc comments.
      return Promise.all(rawFiles.map((f) => Promise.all([computeUcMd5Hash(f), extractCaptureMetadata(f)]))).then((results) => {
        const ucHashes = results.map((r) => r[0]);
        const captureMeta = results.map((r) => r[1]);
        return compressEventImagesInChunks(rawFiles).then((files) => {
        const names = files.map((file, index) => uniqueCompressedImageName(rawFiles[index]?.name || file.name || `photo-${index}.jpg`));
        files.forEach((file, index) => onFileCompressed(names[index], file));
        const ucMetadata = names.map((name, index) => ({
          relative_path: name,
          uc_filename: rawFiles[index]?.name || files[index]?.name || '',
          uc_md5_hash: ucHashes[index],
          captured_at: captureMeta[index]?.capturedAt || null,
          exif_tags: captureMeta[index]?.exifTags || null,
        }));
        if (ucMetadata.length) eventApi.setUcFileMetadata(eventId, ucMetadata, { isSubmission: true }).catch(() => {});
        return lambdaApiClient.post(`/events/${eventId}/photo-submissions/upload-urls`, {
          upload_id: uploadId,
          consent_event_photo_upload: true,
          // Unique-suffixed name (same as EventPage.js's own grouping-upload
          // flow - see uniqueCompressedImageName) rather than the compressed
          // file's own deterministic name, so two submissions that happen to
          // share an original filename (very likely across different
          // visitors' phones) don't display identically in the organiser's
          // Photo Submissions panel or collide in a future bulk export - the
          // actual S3 key is already a random submission_id regardless.
          files: files.map((file, index) => ({
            name: names[index],
            size: file.size,
            content_type: file.type || 'image/jpeg',
          })),
        }, { signal }).then(async r => {
          const uploads = r.data.uploads || [];
          const uploaded = [];
          await Promise.all(uploads.map(async (upload, index) => {
            const file = files[Number.isInteger(upload.index) ? upload.index : index];
            if (!file) return;
            const res = await fetch(upload.url, {
              method: 'PUT',
              headers: { 'Content-Type': upload.content_type || 'image/jpeg' },
              body: file,
              signal,
            });
            if (!res.ok) throw new Error(`Failed to upload ${upload.name || file.name}`);
            uploaded.push(upload);
            onFileUploaded(upload.name);
          }));
          if (!uploaded.length) throw new Error('No photos were uploaded');
          const complete = await lambdaApiClient.post(`/events/${eventId}/photo-submissions`, {
            upload_id: r.data.upload_id || uploadId,
            uploads: uploaded,
          }, { signal });
          return complete.data;
        });
        });
      });
    },
  listPhotoSubmissions: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/photo-submissions`).then(r => r.data),
  deletePhotoSubmission: (eventId, submissionId) =>
    lambdaApiClient.delete(`/events/${eventId}/photo-submissions/${encodeURIComponent(submissionId)}`).then(r => r.data),
  // Backs PhotoSubmissionsPanel's multi-select "Delete selected" action -
  // one request instead of N individual deletePhotoSubmission calls.
  bulkDeletePhotoSubmissions: (eventId, submissionIds) =>
    lambdaApiClient.post(`/events/${eventId}/photo-submissions/bulk-delete`, { submission_ids: submissionIds }).then(r => r.data),
  // Ingest follows the same dispatch chain as initial/add grouping (initiate-grouping
  // reserves credits + sets DB state, grouping-pre-warm launches the server,
  // grouping-kickoff/add-photos-kickoff proxies/forwards the start signal). The photos
  // already live in S3; the grouping server copies them server-side from their
  // submission keys into the standard grouping/add-photos prefix.
  //
  // `mode` is only a hint (caller's best guess, from hasExistingGallery, at whether the
  // event has ever been grouped) - mirrors groupSubmittedPhotosViaLink's own comment:
  // the server is the source of truth and can coerce a stale 'initial' request to 'add'
  // (or vice versa), so every call after initiate must use the *actual* grouping_job_type
  // it reserved, not the original hint, or the kickoff endpoint picked here would mismatch
  // the job type stamped in the DB and silently no-op.
  ingestPhotoSubmissions: async (eventId, submissionIds, mode = 'initial') => {
    console.log(`[EventAPI] ingestPhotoSubmissions: event=${eventId} mode=${mode} submissions=${submissionIds.length}`);
    const hold = await eventApi.initiateGrouping(eventId, mode);
    const actualMode = hold.grouping_job_type === 'add' ? 'add' : 'initial';
    if (actualMode !== mode) console.log(`[EventAPI] ingestPhotoSubmissions: event=${eventId} server coerced mode ${mode} -> ${actualMode}`);
    await eventApi.preWarmGroupingServer(eventId, actualMode);
    const kickoffPayload = {
      mode: actualMode,
      source_mode: 'photo-submissions',
      source_directory: 'photo-submissions',
      submission_ids: submissionIds,
      upload_id: `submissions-${Date.now()}`,
      credit_hold_id: hold.credit_hold_id,
    };
    const kickoffResult = await (actualMode === 'add'
      ? eventApi.addPhotosKickoff(eventId, kickoffPayload)
      : eventApi.groupingKickoff(eventId, kickoffPayload));
    // Callers (AddPhotosSection/SmartSortSection's "Start grouping" buttons)
    // need the server-CONFIRMED mode, not their own local hasExistingGallery
    // guess, for the onStatusChange({grouping_job_type}) call that follows -
    // using a stale/wrong local guess there left a caller's own completion-
    // polling gated on the wrong job type and never resolving.
    return { ...kickoffResult, actual_mode: actualMode };
  },
  // ── Upload links (shareable, permission-gated event-level submit links) ──
  createUploadLink: (eventId, { label, can_trigger_grouping, auto_share_new_submissions } = {}) =>
    lambdaApiClient.post(`/events/${eventId}/upload-links`, { label, can_trigger_grouping, auto_share_new_submissions }).then(r => r.data),
  listUploadLinks: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/upload-links`).then(r => r.data),
  updateUploadLink: (eventId, linkId, patch) =>
    lambdaApiClient.patch(`/events/${eventId}/upload-links/${linkId}`, patch).then(r => r.data),
  revokeUploadLink: (eventId, linkId) =>
    lambdaApiClient.delete(`/events/${eventId}/upload-links/${linkId}`).then(r => r.data),
  // Public: resolves the token to event context for the landing page. No auth.
  getUploadLinkPageData: (token) =>
    lambdaApiClient.get(`/upload-link/${encodeURIComponent(token)}`).then(r => r.data),
  // Get-or-create the event's default (upload-only) link - used by the
  // attendee's Event tab "Submit Photos" button, which has no admin context
  // to pick from the organiser's possibly-many custom links.
  getDefaultUploadLink: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/default-upload-link`).then(r => r.data),

  // Anonymous/link-token-authenticated photo submission - same shape as
  // uploadPhotoSubmissions above, but tags every call with upload_link_token
  // and explicit submitter identity fields instead of relying on an
  // organiser/attendee JWT + require_event. `uploader` is {name, email, phone};
  // the axios interceptor still auto-attaches a personal JWT when the visitor
  // happens to be logged in, so customer_id gets recorded too (see _resolve_submission_access).
  // `sessionId` is the per-link anonymous session id persisted client-side
  // (see UploadLinkPage.js) - lets an anonymous visitor's own submissions be
  // found again via getMyUploadLinkSubmissions after a refresh; harmless to
  // send even when the visitor is logged in.
  // `onProgress({ phase: 'compressing'|'uploading', completed, total })`, if
  // given, fires as each compression chunk and each individual upload
  // completes, so the caller can drive a determinate progress bar.
  uploadPhotoSubmissionsViaLink: async (eventId, rawFiles, uploadLinkToken, uploader = {}, sessionId = '', onProgress, signal, hooks = {}) => {
    const onFileCompressed = hooks.onFileCompressed || (() => {});
    const onFileUploaded = hooks.onFileUploaded || (() => {});
    const uploadId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    console.log(`[UploadLink] uploadPhotoSubmissionsViaLink: event=${eventId} uploadId=${uploadId} files=${rawFiles.length}`);
    // Hash + read capture date/EXIF from each file's RAW bytes before
    // compressEventImagesInChunks touches them - compression is lossy/
    // irreversible, so this is the last point the original's true identity
    // (and its EXIF block) is available. See computeUcMd5Hash's/
    // extractCaptureMetadata's own doc comments.
    const ucHashes = await Promise.all(rawFiles.map((f) => computeUcMd5Hash(f)));
    const captureMeta = await Promise.all(rawFiles.map((f) => extractCaptureMetadata(f)));
    // Same client-side compression as uploadPhotoSubmissions above - see its
    // comment. Just as important here, since anonymous visitors are the most
    // likely to upload straight from a phone camera at full resolution, and
    // this is the function auto-sync batches (up to 100 photos) flow through.
    const files = await compressEventImagesInChunks(rawFiles, undefined, (p) => onProgress?.({ phase: 'compressing', ...p }));
    console.log(`[UploadLink] uploadPhotoSubmissionsViaLink: event=${eventId} uploadId=${uploadId} compression done, ${files.length} file(s), requesting upload URLs`);
    const names = files.map((file, index) => uniqueCompressedImageName(rawFiles[index]?.name || file.name || `photo-${index}.jpg`));
    files.forEach((file, index) => onFileCompressed(names[index], file));
    const ucMetadata = names.map((name, index) => ({
      relative_path: name,
      uc_filename: rawFiles[index]?.name || files[index]?.name || '',
      uc_md5_hash: ucHashes[index],
      captured_at: captureMeta[index]?.capturedAt || null,
      exif_tags: captureMeta[index]?.exifTags || null,
    }));
    if (ucMetadata.length) {
      eventApi.setUcFileMetadata(eventId, ucMetadata, {
        uploadLinkToken, isSubmission: true, uploaderName: uploader?.name || '',
      }).catch(() => {});
    }
    return lambdaApiClient.post(`/events/${eventId}/photo-submissions/upload-urls`, {
      upload_id: uploadId,
      upload_link_token: uploadLinkToken,
      consent_event_photo_upload: true,
      // Unique-suffixed name - see the matching comment on uploadPhotoSubmissions
      // above. Especially relevant here: an upload link is shared across many
      // different visitors/devices, so original-filename collisions ("IMG_0001.jpg"
      // from two different phones) are the common case, not the exception.
      files: files.map((file, index) => ({
        name: names[index],
        size: file.size,
        content_type: file.type || 'image/jpeg',
      })),
    }, { signal }).then(async r => {
      const uploads = r.data.uploads || [];
      console.log(`[UploadLink] uploadPhotoSubmissionsViaLink: event=${eventId} uploadId=${uploadId} got ${uploads.length} upload URL(s), starting S3 PUTs`);
      const uploaded = [];
      let uploadedCount = 0;
      await Promise.all(uploads.map(async (upload, index) => {
        const file = files[Number.isInteger(upload.index) ? upload.index : index];
        if (!file) return;
        const res = await fetch(upload.url, {
          method: 'PUT',
          headers: { 'Content-Type': upload.content_type || 'image/jpeg' },
          body: file,
          signal,
        });
        if (!res.ok) {
          console.error(`[UploadLink] uploadPhotoSubmissionsViaLink: S3 PUT failed for ${upload.name || file.name} (HTTP ${res.status})`);
          throw new Error(`Failed to upload ${upload.name || file.name}`);
        }
        uploaded.push(upload);
        onFileUploaded(upload.name);
        uploadedCount += 1;
        onProgress?.({ phase: 'uploading', completed: uploadedCount, total: uploads.length });
      }));
      if (!uploaded.length) {
        console.error(`[UploadLink] uploadPhotoSubmissionsViaLink: event=${eventId} uploadId=${uploadId} - no photos uploaded, aborting`);
        throw new Error('No photos were uploaded');
      }
      console.log(`[UploadLink] uploadPhotoSubmissionsViaLink: event=${eventId} uploadId=${uploadId} all ${uploaded.length} file(s) PUT to S3, finalizing submission`);
      const complete = await lambdaApiClient.post(`/events/${eventId}/photo-submissions`, {
        upload_id: r.data.upload_id || uploadId,
        upload_link_token: uploadLinkToken,
        uploads: uploaded,
        uploader_name: uploader.name || '',
        uploader_email: uploader.email || '',
        uploader_phone: uploader.phone || '',
        submitter_session_id: sessionId || '',
      }, { signal });
      console.log(`[UploadLink] uploadPhotoSubmissionsViaLink: event=${eventId} uploadId=${uploadId} submission complete, submissionIds=${(complete.data?.submission_ids || []).length}`);
      return complete.data;
    }).catch((err) => {
      console.error(`[UploadLink] uploadPhotoSubmissionsViaLink: event=${eventId} uploadId=${uploadId} FAILED:`, err?.message || err);
      throw err;
    });
  },
  // Lists the caller's own submissions for this event via the link (reachable
  // even for a logged-in visitor who has never joined the event - see
  // list_my_submissions_via_link). Used to repopulate the upload-link page's
  // "already submitted" gallery after a refresh.
  getMyUploadLinkSubmissions: (eventId, uploadLinkToken, sessionId = '') =>
    lambdaApiClient.get(`/events/${eventId}/photo-submissions/mine-via-link`, {
      params: { upload_link_token: uploadLinkToken, submitter_session_id: sessionId || '' },
    }).then(r => r.data),
  // Batch-resolves presigned URLs for the caller's own link-submitted photo
  // thumbnails - mirrors getPhotoSubmissionPresignedUrls/getMyPhotoSubmissionPresignedUrls
  // but scoped via upload_link_token + customer_id-or-submitter_session_id,
  // same access model as getMyUploadLinkSubmissions above. Use as an <img src>
  // instead of ever hitting the per-photo photo-via-link route directly.
  getUploadLinkSubmissionPresignedUrls: (eventId, submissionIds, uploadLinkToken, sessionId = '') =>
    lambdaApiClient.post(`/events/${eventId}/photo-submissions/presigned-urls-via-link`, {
      submission_ids: submissionIds,
      upload_link_token: uploadLinkToken,
      submitter_session_id: sessionId || '',
    }).then(r => r.data),

  // Link-token-authenticated grouping trigger. `mode` is 'initial' or 'add' -
  // callers decide which based on whether the event already has a gallery
  // (hasExistingGallery in EventPage.js's own logic, mirrored on the link page).
  initiateGroupingViaLink: (eventId, mode, uploadLinkToken) =>
    _apiLog(`initiateGroupingViaLink event=${eventId} mode=${mode}`,
      lambdaApiClient.post(`/events/${eventId}/initiate-grouping`, { mode, upload_link_token: uploadLinkToken }).then(r => r.data)),
  preWarmGroupingServerViaLink: (eventId, mode, uploadLinkToken) =>
    _apiLog(`preWarmGroupingServerViaLink event=${eventId} mode=${mode}`,
      lambdaApiClient.post(`/events/${eventId}/grouping-pre-warm`, { mode, upload_link_token: uploadLinkToken }).then(r => r.data)),
  getGroupingStatusViaLink: (eventId, uploadLinkToken) =>
    lambdaApiClient.get(`/events/${eventId}/grouping-status`, { params: { upload_link_token: uploadLinkToken } }).then(r => r.data),
  // Cancels whatever grouping job is currently active for this event (releases
  // the credit hold, stops the compute instance if one is running) - same
  // per-event, not per-caller, semantics as the organiser's own cancel button.
  // Only meaningful once a job is confirmed actually running (see
  // UploadLinkPage.js's groupingConfirmedActiveRef) - calling this when
  // nothing is active would incorrectly reset the event's grouping_status.
  cancelGroupingViaLink: (eventId, uploadLinkToken) =>
    _apiLog(`cancelGroupingViaLink event=${eventId}`,
      lambdaApiClient.post(`/events/${eventId}/cancel-grouping`, { upload_link_token: uploadLinkToken }).then(r => r.data)),
  // Mirrors ingestPhotoSubmissions (organiser's "group from submitted photos"
  // button) but works for both the very first grouping job (mode='initial')
  // and incremental add-photos jobs (mode='add'), billed to the link's event
  // organiser regardless of who - or whether anyone - is logged in.
  //
  // `mode` here is only ever a *hint* (the link page's best guess, from its own
  // page-load snapshot, at whether the event already has a gallery). The
  // server is the source of truth: if another concurrent session already
  // completed the first grouping run since this page loaded, initiate-grouping
  // silently coerces a stale 'initial' request to 'add' (see grouping.py's
  // grouping_actor_required/_reserve_grouping_credit_hold) and returns the
  // *actual* grouping_job_type it reserved. Every call after initiate must use
  // that authoritative value, not the original hint, or the kickoff endpoint
  // picked here would mismatch the job type stamped in the DB and silently
  // no-op (the atomic claim's WHERE clause wouldn't match).
  groupSubmittedPhotosViaLink: async (eventId, submissionIds, mode, uploadLinkToken) => {
    console.log(`[UploadLink] groupSubmittedPhotosViaLink: event=${eventId} mode=${mode} submissions=${submissionIds.length}`);
    const hold = await eventApi.initiateGroupingViaLink(eventId, mode, uploadLinkToken);
    const actualMode = hold.grouping_job_type === 'add' ? 'add' : 'initial';
    if (actualMode !== mode) console.log(`[UploadLink] groupSubmittedPhotosViaLink: event=${eventId} server coerced mode ${mode} -> ${actualMode}`);
    await eventApi.preWarmGroupingServerViaLink(eventId, actualMode, uploadLinkToken);
    const kickoffPayload = {
      mode: actualMode,
      source_mode: 'photo-submissions',
      source_directory: 'photo-submissions',
      submission_ids: submissionIds,
      upload_id: `submissions-${Date.now()}`,
      credit_hold_id: hold.credit_hold_id,
      upload_link_token: uploadLinkToken,
    };
    return actualMode === 'add'
      ? eventApi.addPhotosKickoff(eventId, kickoffPayload)
      : eventApi.groupingKickoff(eventId, kickoffPayload);
  },

  // Batch-resolves presigned URLs for many submission thumbnails in one call -
  // use as an <img src> instead of ever hitting the per-photo backend route
  // directly, since that fires one Lambda invocation + DB round-trip per photo.
  getPhotoSubmissionPresignedUrls: (eventId, submissionIds) =>
    lambdaApiClient.post(`/events/${eventId}/photo-submissions/presigned-urls`, {
      submission_ids: submissionIds,
    }).then(r => r.data),
  getMyPhotoSubmissions: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/my-photo-submissions`).then(r => r.data),
  deleteMyPhotoSubmission: (eventId, submissionId) =>
    lambdaApiClient.delete(`/events/${eventId}/my-photo-submissions/${encodeURIComponent(submissionId)}`).then(r => r.data),
  // Batch-resolves presigned URLs for the caller's own submission thumbnails -
  // mirrors getPhotoSubmissionPresignedUrls above but scoped to this account.
  getMyPhotoSubmissionPresignedUrls: (eventId, submissionIds) =>
    lambdaApiClient.post(`/events/${eventId}/my-photo-submissions/presigned-urls`, {
      submission_ids: submissionIds,
    }).then(r => r.data),

  // ── Event feedback ────────────────────────────────────────────────────────
  submitFeedback: (eventId, payload) =>
    lambdaApiClient.post(`/events/${eventId}/feedback`, payload).then(r => r.data),
  getFeedback: (eventId) =>
    lambdaApiClient.get(`/events/${eventId}/feedback`).then(r => r.data),
  deleteFeedback: (eventId, feedbackId) =>
    lambdaApiClient.delete(`/events/${eventId}/feedback/${encodeURIComponent(feedbackId)}`).then(r => r.data),

  // ── Event tools: merch orders ─────────────────────────────────────────────
  orderEventMerch: (eventId, payload) =>
    lambdaApiClient.post(`/events/${eventId}/merch-order`, payload).then(r => r.data),

  updateMyRequest: async (eventId, formData) => {
    const referencePhoto = formData.get('reference_photo');
    let upload = null;
    if (referencePhoto) {
      const current = await eventApi.getMyRequest(eventId);
      upload = await _uploadPhotoRequestReference(eventId, referencePhoto, {
        requestId: current?.request_id || '',
      });
    }
    const res = await fetch(`${LAMBDA_BACKEND_URL}/api/events/${eventId}/my-request`, {
      method: 'PUT',
      headers: _authHeaders(true),
      body: JSON.stringify(_photoRequestPayload(formData, upload ? {
        reference_photo_key: upload.upload.key,
      } : {})),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.message || 'Failed to update submission');
    return data;
  },
};

export default eventApi;
